import styled from "@emotion/styled";
import searchIcon from "../../icons/search-icon-blue.svg";

/* Header box model mirrors bkvenergy.com: #ffffff background, z-index 9999.
   Parent is position:fixed; ours stays sticky because the page has no spacer/offset for a
   fixed bar - switching would slide 64px of content under the header. Sticky is visually
   identical here (the bar is at the top of the document and stays pinned). */
export const HeaderStyled = styled.div`
  position: sticky;
  top: 0;
  background-color: #ffffff;
  z-index: 9999;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);

  padding: 0 15px;
  height: 64px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  /* "futura-pt" comes from BKV's Typekit kit (real 300/400/500/600); the bundled
     "Futura PT Book" is a single 400 file and is only the fallback */
  font-family: "futura-pt", "Futura PT Book", sans-serif;
  .logo {
    width: 120px;
    height: 48px;
    cursor: pointer;
  }

  a {
    text-decoration: none;
    color: #09507f;
    text-transform: uppercase;
    font-size: 15px;
  }

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(
      103deg,
      #ffd27d 0%,
      #87c674 15%,
      #139e87 62%,
      #09507f 100%
    );
    z-index: 9999;
  }

  @media (max-width: 992px) {
    .logo {
      width: 105px;
      height: 42px;
    }
  }

  @media (max-width: 768px) {
    height: 64px;
    padding: 0 15px;
    .logo {
      width: 90px;
      height: 36px;
    }
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* parent .container ladder 1654/1390/1150/unset + nav.main-nav gap ladder 30/15/10/8
     - this is the nav -> phone/button gap */
  gap: 30px;
  width: 100%;
  max-width: 1654px;
  padding: 0 15px;
  /* parent's .container is border-box: max-width includes the padding */
  box-sizing: border-box;
  @media (max-width: 1720px) {
    gap: 15px;
    max-width: 1390px;
  }
  @media (max-width: 1440px) {
    gap: 10px;
    max-width: 1150px;
  }
  @media (max-width: 1200px) {
    gap: 8px;
    max-width: unset;
    padding: 0 20px;
  }
  img:hover {
    cursor: pointer;
  }
`;

export const HeaderTitleItems = styled.div`
  display: flex;
  /* parent ul.navbar-nav gap ladder: 20 / 10 / 15 / 10 */
  gap: 20px;
  justify-content: space-evenly;
  height: 100%;
  /* parent ul.navbar-nav carries margin-right:85px at base only */
  margin-right: 85px;
  /* parent's nav.main-nav is margin-left:auto, so the cluster hugs the CTA */
  margin-left: auto;
  @media (max-width: 1720px) {
    gap: 10px;
    margin-right: 0;
  }
  @media (max-width: 1440px) {
    gap: 15px;
  }
  @media (max-width: 1200px) {
    gap: 10px;
  }
`;

export const HeaderTitleItem = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0;
  a {
    display: flex;
    align-items: center;
    height: 100%;
    box-sizing: border-box;
    /* parent .navbar-nav > li > a: 15px + 16px base, 15px 5px + 15px @1440 */
    padding: 15px;
    line-height: 29px;
    font-size: 16px;
    text-underline-offset: 5px;
    @media (max-width: 1440px) {
      padding: 15px 5px;
      font-size: 15px;
    }
  }
  a:hover {
    text-decoration: underline !important;
  }
  /* parent keeps the label underlined while the pointer is inside the open panel
     (.navbar-nav > li:has(> ul:hover) > a). Ours is a sibling drawer rather than a nested
     <ul>, so the underline is held through this class for the item whose panel is open -
     this also covers the parent's li.current-menu-item / li.current-menu-parent case. */
  &.active a {
    text-decoration: underline !important;
  }
  a[aria-expanded="true"] {
    text-decoration: underline !important;
  }
`;

export const RightSection = styled.div`
  display: flex;
  align-items: center;
  /* anchors the absolutely-positioned search box so it can grow leftward
     without reflowing the nav/CTA (parent anchors it in nav.main-nav) */
  position: relative;
  /* parent nav.main-nav carries padding-right:55px, which is what sets the
     button's 77px inset from the container edge (55 + 15 container + 7 btn margin) */
  padding-right: 55px;
  @media (max-width: 992px) {
    padding-right: 0;
  }
  /* same gap ladder as nav.main-nav */
  gap: 30px;
  @media (max-width: 1720px) {
    gap: 15px;
  }
  @media (max-width: 1440px) {
    gap: 10px;
  }
  @media (max-width: 1200px) {
    gap: 8px;
  }
  /* phone CTA - parent's .main-nav__cta--phone: 16px/29px weight 500, pointer */
  a {
    font-size: 16px;
    line-height: 29px;
    font-weight: 500;
    cursor: pointer;
    /* parent .main-nav__cta is margin:0 7px */
    margin: 0 7px;
  }
  a:hover {
    color: #00b49d;
  }
  /* scoped to the direct child so this cannot reach the search button that sits
     nested inside its own form. An unscoped descendant button rule here wiped the
     search icon on hover (the background shorthand reset background-image) and
     applied the CTA scale animation to it. */
  > button {
    border-radius: 27px;
    transition: all 0.1s ease-in-out;
    border: 3px solid #ffd27d;
    background: #ffd27d;
    padding: 9px 38px;
    min-width: 190px;
    margin: 0 7px;
    box-sizing: border-box;
    font-family: "futura-pt", sans-serif;
    font-size: 15px;
    font-weight: 600;
    line-height: 23px;
    cursor: pointer;
    /* Parent's real ladder. Measured on bkvenergy.com with a fresh load at every width:
       the CTA is 47px tall (9px/9px block padding + 23px line-height + 6px border) at
       375, 768, 992, 1200, 1440, 1920 - it never shrinks.
       Why the authored <=1200 block (font-size 12px; line-height 16px; padding 7px 10px)
       does NOT apply: @media (max-width:1200px) .button is (0,1,0) while
       .main-nav .button { font-size: 15px } is (0,2,0), so the larger base wins and only
       the parts of the <=1200 rule that are more specific (min-width, 0,3,0) take effect.
       Carrying that block over is what made our CTA collapse to 36px below 1200. */
    @media (max-width: 1720px) {
      padding-inline: 13px;
    }
    @media (max-width: 1200px) {
      min-width: unset;
    }
    /* MUI <Typography> renders a <p> with .MuiTypography-body1 (font-size:0.8rem=12.8px);
       inheriting from the button outranks it and keeps the ladder in one place */
    p {
      margin: 0;
      font-family: inherit;
      font-size: inherit;
      font-weight: 600;
      line-height: inherit;
      text-transform: uppercase;
    }
    &:hover {
      animation: scale 0.2s ease-in-out;
      background: transparent;
    }
    @keyframes scale {
      from {
        transform: scale(1);
      }
      to {
        transform: scale(1.1);
      }
    }
  }

  /* same boundary as the nav collapse so the CTA keeps parent's margin: 0 7px */
  @media (max-width: 992px) {
    > button {
      margin-left: 0;
    }
  }
`;

/* Search control mirroring bkvenergy.com's header search.
   Every value below is taken from the parent's own author CSS, which is:
     form.search-form    position absolute; right 0; top 50%; translateY(-50%); transition width .3s
     .search-field       border 0; width 40px; height 40px; radius 31px; opacity 0; position relative;
                         z-index 2; cursor pointer; color rgb(9,78,128)
     .search-field.search-input-opened
                         padding 14px 45px 15px 26px; background #fff; border 1px solid rgb(23,180,157);
                         width 424px; opacity 1; cursor default; font-size 18px
     .search-field::placeholder
                         color rgb(9,78,128); text-transform uppercase; opacity 0, 1 once open
     input[type=submit]  position absolute; right 5px; top 0; bottom 0; 40x40; radius 31px;
                         background-image <own filled svg>, centred; cursor pointer; z-index 5
   The submit anchors to the FIELD (the field is position:relative), which is why the field
   reserves 45px of right padding once open - the glyph sits inside the pill.
   The form is position:absolute, so opening reflows nothing.
   On the parent the field is never removed: it stays in the DOM at opacity 0 and only its
   width/opacity change, which is what makes the 0.3s width transition read correctly.
   The mobile drawer is the parent's <=992px case: field permanently open, 350px (100% <=576). */
export const SearchBoxStyled = styled.form`
  display: block;
  box-sizing: border-box;
  ${({ $wide }) =>
    $wide
      ? `width: 100%; margin-top: 8px;`
      : `
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    transition: width 0.3s;
    z-index: 20;
  `}
`;

export const SearchField = styled.input`
  box-sizing: border-box;
  display: inline-block;
  position: relative;
  z-index: 2;
  width: ${({ $open, $wide }) => ($open ? ($wide ? "100%" : "424px") : "40px")};
  max-width: 100%;
  height: 40px;
  margin: 0;
  padding: ${({ $open }) => ($open ? "14px 45px 15px 26px" : "0")};
  border: ${({ $open }) => ($open ? "1px solid #17b49d" : "0")};
  border-radius: 31px;
  background-color: ${({ $open }) => ($open ? "#ffffff" : "transparent")};
  color: #094e80;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  cursor: ${({ $open }) => ($open ? "default" : "pointer")};
  transition: 0.3s;
  outline: none;
  font-family: "futura-pt", sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: normal;
  text-transform: none;
  &::placeholder {
    color: #094e80;
    text-transform: uppercase;
    letter-spacing: normal;
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    transition: opacity 0.3s;
  }
  &:focus {
    border-color: #17b49d;
  }
  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 20px;
  }
  /* parent's own <=992px (drawer) block: the field there is a fixed 350px, and it
     carries its own placeholder treatment - 14px, rgb(9,80,127) - not the desktop one.
     <=576px the parent narrows the opened field to 235px. */
  ${({ $wide }) =>
    $wide
      ? `
  @media (max-width: 992px) {
    width: 350px;
    &::placeholder {
      opacity: 1;
      font-size: 14px;
      color: #09507f;
    }
  }
  @media (max-width: 576px) {
    width: 235px;
  }
  `
      : ""}
`;

/* Doubles the class specificity so it outranks RightSection's descendant
   button rule (0,2,0 beats 0,1,1). The && selector is intentional.
   Mirrors bkvenergy.com's input.search-submit: 40x40, radius 31px, pinned to the
   form's right edge (on the parent it never moves between states - the field grows
   leftward underneath it), glyph painted as a centred background-image.
   z-index 5 keeps it above the field (z-index 2) while the field is transparent. */
export const SearchIconBtn = styled.button`
  /* reset CTA styling (border/bg/padding/min-width) applied by RightSection */
  && {
    box-sizing: border-box;
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    border: 0;
    padding: 0;
    margin: 0;
    min-width: unset;
    width: 40px;
    height: 40px;
    border-radius: 31px;
    display: block;
    cursor: pointer;
    /* parent's input.search-submit declares transition:all with 0s duration and
       has no visual hover change - mirrored exactly so the two read identically */
    transition: all;
    z-index: 5;
    background-color: transparent;
    background-image: url(${searchIcon});
    background-repeat: no-repeat;
    background-position: 50% 50%;
    &:focus-visible {
      outline: 2px solid #17b49d;
      outline-offset: 2px;
    }
  }
`;

export const Drawer = styled.div`
  position: fixed;
  /* parent's panel is width:100vw, so it spans the full viewport INCLUDING the scrollbar
     gutter (1920 at a 1920 window). left/right:0 would instead measure clientWidth (1905)
     and shift the whole centred row 7px left. */
  left: 0;
  width: 100vw;
  box-sizing: border-box;
  /* parent: padding 48px 0, height auto (measures 477px at 1440, 547px at <=1200) */
  padding: 48px 0;
  box-sizing: border-box;
  height: auto;
  background-color: #ffffff;
  /* parent's panel carries no shadow */
  box-shadow: none;
  /* parent: .2s ease-in all */
  transition: 0.2s ease-in all;
  z-index: 10;
  overflow: visible;
  /* the drawer renders outside #header, so it must declare the font itself */
  font-family: "futura-pt", "Futura PT Book", sans-serif;
  &.open {
    top: 64px;
    pointer-events: auto;
    opacity: 1;
    /* parent keeps the panel in the layout when closed (visibility:visible) so the
       hover -> open transition can run; only opacity/pointer-events gate it */
    visibility: visible;
  }

  &.close {
    /* parent closes from top:220px - a 156px travel, not our old 64px */
    top: 220px;
    opacity: 0;
    visibility: visible;
    pointer-events: none;
  }
`;

/* parent's .container-narrow ladder, measured 993 -> 1920:
     1920 -> 1394 · 1720/1600 -> 1130 · 1440 and below -> 942
   Authored as a max-width ladder (base 1394, narrowing down), not min-width. */
export const DrawerContent = styled.div`
  width: 100%;
  max-width: 1394px;
  /* parent wraps its .wp-block-columns in <li class="menu-item"> with margin: 0 0 12px,
     so the panel measures 48 + 369 + 12 + 48 = 477px @1440 (547px <=1200).
     Without the 12px the panel is short and the bottom edge sits 12px high. */
  margin: 0 auto 12px;
  padding: 0 15px;
  box-sizing: border-box;
  /* Column heading - measured @1440 from the parent's markup:
       <p class="wp-block-paragraph">           20px/29px, margin-bottom 15px  -> 32px tall box
         <strong><a>Plans</a></strong>           16px/29px, 500, #00b49d, underline -> 16px inline box
     The underlined link is the <strong>'s <a>, and the 32px wrapper is what pushes the first
     item down: heading top 112 -> first item top 159 = 47px (32px box + 15px margin). */
  .link-title {
    display: flex;
    align-items: center;
    font-family: "Larken DEMO medium", "Larken DEMO", Georgia, serif;
    color: #00b49d;
    font-size: 16px;
    line-height: 29px;
    height: 32px;
    box-sizing: border-box;
    text-decoration: underline;
    text-underline-offset: 3px;
    font-weight: 500;
    text-transform: none;
    /* parent: heading <p> margin-bottom 15px */
    margin: 0 0 15px;
  }
  /* headingless column: holds the heading's 32px box + 15px so the columns' first items
     stay on the same baseline - the parent ships a hidden <p> of exactly those dimensions */
  .link-title-spacer {
    display: block;
    height: 32px;
    margin: 0 0 15px;
  }
  .link-title:hover {
    color: #09507f;
  }
  a {
    text-decoration: none;
    color: #09507f;
    text-transform: uppercase;
    font-size: 16px;
    line-height: 26px;
    font-family: inherit;
    display: block;
  }
  a:hover {
    color: #00b49d;
  }
  @media (max-width: 1720px) {
    max-width: 1130px;
  }
  @media (max-width: 1440px) {
    max-width: 942px;
  }
  /* parent widens its item line-height to 36px <=1200 */
  @media (max-width: 1200px) {
    a {
      line-height: 36px;
    }
  }
`;

/* parent's .wp-block-columns: equal-split columns, 38px gutter (32px <=1200) */
export const DrawerRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 38px;
  @media (max-width: 1200px) {
    gap: 32px;
  }
  /* parent stacks to one column at the 992px nav collapse */
  @media (max-width: 992px) {
    flex-direction: column;
    gap: 0;
  }
`;

export const DrawerLink = styled.div`
  /* parent item pitch = 46px (26px line-height + 20px) - must be per-item margin,
     not flex gap, or it also inflates the heading -> first-item gap */
  margin-bottom: 20px;
`;

export const DrawerLinks = styled.div`
  display: flex;
  flex-direction: column;
  /* parent .wp-block-column: equal split, never content-sized */
  flex-basis: 100%;
  flex-grow: 1;
  max-width: 350px;
  margin-bottom: 0;
  @media (max-width: 992px) {
    /* parent stacks here and drops the max-width */
    max-width: unset;
    width: 100%;
    margin-bottom: 15px;
  }
`;


export const MobileDrawerSection = styled.div`
    /* parent <=992 #megaNavbarNavDropdown: a WHITE 400px off-canvas column (100% <=576),
       height calc(100vh - 64px), z-index 2. The grey #f7f7f7 belongs to the inner
       sub-panel (see Accordion below), not this shell. */
    width: 400px;
    max-width: 100vw;
    height: calc( 100vh - 64px);
    background: #fff;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    z-index: 2;
    position: absolute;
    top: 64px;
    right: 0;
    /* parent > ul: padding-left 20px, padding-top 45px, padding-bottom 90px, overflow-y scroll */
    padding: 45px 20px 90px;
    @media (max-width: 576px) {
      width: 100%;
      padding-top: 0;
    }
    /* the expanded list can outrun the viewport on a phone */
    overflow-y: auto;

    &.open {
      visibility: visible;
      opacity: 1;
      transition: visibility 0s, opacity 0.5s linear;
    }

    &.close {
        visibility: hidden;
        opacity: 0;
        transition: visibility 0.6s, opacity 0.5s linear;
    }
`;

export const MobileDrawerTitle = styled.div`
    font-size: 18px;
    padding: 15px 5px;
    background:#fff;
    color: #09507f;
    font-family: "futura-pt", "Futura PT Book", sans-serif;
    display: flex;
    justify-content: space-between;
    padding-right: 50px;
`

export const Accordion = styled.div`
    /* parent <=992 nested sub-menu: grey #f7f7f7 inset panel, width 385px (100vw <=576),
       left -30px, padding 20px 100px 20px 35px, no radius, display:none until opened */
    position: relative;
    left: -30px;
    width: 385px;
    max-width: 100vw;
    box-sizing: border-box;
    padding: 20px 100px 20px 35px;
    background: #f7f7f7;
    border-radius: 0;
    z-index: 2;
    @media (max-width: 576px) {
      width: 100vw;
    }

    &.accordion-open {
      display: block;
      visibility: visible;
      height: 100%;
      opacity: 1;
      transition:height 0.6s, opacity 0.6s linear;
    }

    &.accordion-close {
      display: none;
      visibility: hidden;
      height: 0;
      opacity: 0;
      transition: height 0.6s, visibility 0.3s, opacity 1s linear;
    }
`

export const AccordionLink = styled.div`
  /* parent mobile item pitch = 24px line-box + 15px = 39px (>=768) */
  margin-bottom: 15px;
`

export const AccordionLinks = styled.div`
  font-family: "futura-pt", "Futura PT Book", sans-serif;
  /* Mobile heading - measured @992/@768: <p> 20px/29px, box 32px, mb 15px;
     @<=576: 16px/23px, box 25px, mb 15px. The visible text is always the 16px underlined
     <strong><a>; the taller wrapper is what offsets the first item. */
  .link-title {
    display: flex;
    align-items: center;
    font-family: "Larken DEMO medium", "Larken DEMO", Georgia, serif;
    color: #00b49d;
    font-size: 16px;
    line-height: 29px;
    height: 32px;
    box-sizing: border-box;
    text-decoration: underline;
    text-underline-offset: 3px;
    font-weight: 500;
    text-transform: none;
    margin-bottom: 15px;
    @media (max-width: 576px) {
      line-height: 23px;
      height: 25px;
    }
  }
  /* headingless column: holds the heading's line box so columns keep one baseline */
  .link-title-spacer {
    display: block;
    height: 0;
    margin: 0;
  }
  /* parent <=992 hides ONLY the heading <p> of the heading-less column
     (.wp-block-column.two-col-list > p { display:none }) - measured @430 the column still
     renders all 4 links (Fort Worth / Waco / Abilene / View All) as normal list items.
     So collapse the spacer, never the group. */
  @media (max-width: 992px) {
    .link-title-spacer {
      display: none;
    }
  }
  .link-title:hover {
    color: #09507f;
  }
  a {
    text-decoration: none;
    color: #09507f;
    text-transform: uppercase;
    font-family: "futura-pt", "Futura PT Book", sans-serif;
    font-size: 16px;
    /* parent li line-height (measured on the rendered mobile sub-menu):
       24px at 992/768 -> 23px at <=576 */
    line-height: 24px;
    display: block;
    @media (max-width: 576px) {
      line-height: 23px;
    }
  }
  a:hover {
    color: #00b49d;
  }
`
